import {
  fetchAtom as baseFetchAtom,
  type FetchError,
  type Result as BaseResult,
} from "@tokenterminal/core/api"
import { type GETRequests } from "@tokenterminal/tt-analytics-api-types/dist/api-routes"
import { type WritableAtom, atom, useAtomValue } from "jotai"
import { atomFamily } from "jotai/utils"
import { type EndpointsGET, type GetResponseData } from "./get"
export type { EndpointsGET } from "./get"

/**
 * Some explanation how this all works:
 *
 * Our fetchAtom contains 2 steps, one step is allow us to override the cache with a new value, the other step is to fetch the data from the API.
 * So firstly, we fetch from API, whenever we do a refresh() call or a direct write to the atom, we override the cache with the new value.
 * - refreshAtom is the actual call to the api
 * - interceptorAtom is the middleware that allows us to override the cache
 */

export type Result<T> = BaseResult<T, FetchError>
export type { FetchError } from "@tokenterminal/core/api"

export interface RefreshAtom<Value>
  extends WritableAtom<Promise<Result<Value>>, [] | [Value], void> {
  refresh: () => void
}

// export function fetchAtomWithRefresh<
//   T extends EndpointsGET,
//   U = Result<GETRequests[T][0], FetchError>,
// >(endpoint: T): WritableAtom<Promise<U> | U, [], void> {
//   return fetchAtomWithRefreshAtom(endpoint) as WritableAtom<
//     Promise<U> | U,
//     [],
//     void
//   >
// }
const fetchAtomFamily = atomFamily((endpoint: EndpointsGET) => {
  const refreshAtom = baseFetchAtom(endpoint)

  const interceptorAtom = refreshAtom as unknown as RefreshAtom<
    GetResponseData<EndpointsGET>
  >

  interceptorAtom.onMount = function onMount(setAtom) {
    interceptorAtom.refresh = function refresh() {
      setAtom()
    }
  }
  interceptorAtom.refresh = function refresh() {}

  return interceptorAtom
})

export function fetchAtom<T extends EndpointsGET, U = GETRequests[T][0]>(
  endpoint: T
): RefreshAtom<U> {
  return fetchAtomFamily(endpoint) as unknown as RefreshAtom<U>
}

// Or alternatively use
/*
 * const endpointAtom = atom(get(endpoint))
 * useAtomValue(endpointAtom)
 */
export const nullAtom = atom(null)

export function useFetch<T extends EndpointsGET>(
  endpoint: T,
  shouldFetch: true
): GetResponseData<T>
export function useFetch<T extends EndpointsGET>(
  endpoint: T,
  shouldFetch?: undefined,
  allowError?: boolean
): GetResponseData<T>
export function useFetch<T extends EndpointsGET>(
  endpoint: T,
  shouldFetch: false,
  allowError?: boolean
): null
export function useFetch<T extends EndpointsGET>(
  endpoint: T,
  shouldFetch: boolean,
  allowError?: boolean
): GetResponseData<T> | null

export function useFetch<T extends EndpointsGET>(
  endpoint: T,
  shouldFetch = true,
  allowError = false
) {
  const value = useAtomValue(shouldFetch ? fetchAtom(endpoint) : nullAtom)

  if (!shouldFetch) {
    return null
  }

  if (value?.err) {
    if (allowError) {
      return null
    }

    throw value.err
  }

  return value?.ok
}
